import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import Modal from '../../components/common/Modal';
import {
  Users,
  User,
  Phone,
  MapPin,
  Calendar,
  Power,
  Search,
} from 'lucide-react';
import { format } from 'date-fns';

const AdminUsers = () => {
  const { theme } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showToggleModal, setShowToggleModal] = useState(false);

  const [nameFilter, setNameFilter] = useState('');
  const [mobileFilter, setMobileFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [minAttendedFilter, setMinAttendedFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (nameFilter.trim()) params.set('name', nameFilter.trim());
      if (mobileFilter.trim()) params.set('mobile', mobileFilter.trim());
      if (cityFilter.trim()) params.set('city', cityFilter.trim());
      if (minAttendedFilter.trim()) params.set('minAttended', minAttendedFilter.trim());
      if (statusFilter !== 'all') params.set('isActive', String(statusFilter === 'active'));

      const response = await api.get(`/admin/users?${params.toString()}`);
      setUsers(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [statusFilter]);

  const applyFilters = () => {
    fetchUsers();
  };

  const resetFilters = () => {
    setNameFilter('');
    setMobileFilter('');
    setCityFilter('');
    setMinAttendedFilter('');
    setStatusFilter('all');
  };

  const handleToggleStatus = async () => {
    if (!selectedUser) return;

    try {
      await api.patch(`/admin/users/${selectedUser.id}/toggle`, {
        isActive: !selectedUser.isActive,
      });
      setShowToggleModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user status');
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'User',
      render: (user) => (
        <div>
          <div style={{ fontWeight: '600', color: theme.colors.text }}>{user.name}</div>
          <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>{user.email}</div>
        </div>
      ),
    },
    {
      key: 'contact',
      label: 'Mobile',
      render: (user) => (
        user.phone ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.colors.text }}>
            <Phone size={14} color={theme.colors.textSecondary} />
            {user.phone}
          </div>
        ) : <span style={{ color: theme.colors.textSecondary }}>-</span>
      ),
    },
    {
      key: 'location',
      label: 'City / State',
      render: (user) => {
        const locationLine = [user.city, user.state].filter(Boolean).join(', ');
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.colors.text }}>
              <MapPin size={14} color={theme.colors.textSecondary} />
              {locationLine || '-'}
            </div>
            {user.country && (
              <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: '2px' }}>{user.country}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'attendance',
      label: 'Attendance',
      render: (user) => (
        <div>
          <div style={{ color: theme.colors.text, fontWeight: '600' }}>{user.totalAttended || 0}</div>
          <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>Total Attended</div>
        </div>
      ),
    },
    {
      key: 'lastMeeting',
      label: 'Last Meeting Joined',
      render: (user) => (
        <div>
          <div style={{ color: theme.colors.text }}>{user.lastMeetingTitle || '-'}</div>
          <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
            {user.lastMeetingJoinedAt ? format(new Date(user.lastMeetingJoinedAt), 'MMM d, yyyy h:mm a') : '-'}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (user) => (
        <Badge variant={user.isActive ? 'success' : 'danger'} size="sm" status>
          {user.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (user) => (
        <Button
          size="sm"
          variant="ghost"
          icon={Power}
          onClick={() => {
            setSelectedUser(user);
            setShowToggleModal(true);
          }}
          title={user.isActive ? 'Deactivate' : 'Activate'}
        />
      ),
    },
  ];

  const styles = {
    container: {
      padding: '32px',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
      marginBottom: '20px',
    },
    error: {
      marginBottom: '12px',
      padding: '12px',
      borderRadius: '8px',
      backgroundColor: theme.colors.error + '18',
      border: `1px solid ${theme.colors.error}44`,
      color: theme.colors.error,
      fontSize: '14px',
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
      gap: '10px',
      marginBottom: '12px',
    },
    searchWrap: {
      position: 'relative',
      width: '100%',
    },
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: theme.colors.textTertiary,
      pointerEvents: 'none',
    },
    filterInput: {
      width: '100%',
      height: '44px',
      padding: '10px 12px',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.text,
      fontSize: '13px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    searchInput: {
      width: '100%',
      height: '44px',
      padding: '10px 12px 10px 36px',
      borderRadius: '10px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.cardBg,
      color: theme.colors.text,
      fontSize: '13px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    filtersAction: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '10px',
    },
  };

  if (loading) {
    return <LoadingSpinner fullScreen text="Loading users..." />;
  }

  return (
    <div style={styles.container}>
      {error && <div style={styles.error}>{error}</div>}

      <Card compact>
        <div style={styles.filtersGrid}>
          <div style={styles.searchWrap}>
            <Search size={16} style={styles.searchIcon} />
            <input
              style={styles.searchInput}
              placeholder="Filter by name"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
          <input
            style={styles.filterInput}
            placeholder="Filter by mobile"
            value={mobileFilter}
            onChange={(e) => setMobileFilter(e.target.value)}
          />
          <input
            style={styles.filterInput}
            placeholder="Filter by city"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
          />
          <input
            style={styles.filterInput}
            type="number"
            min="0"
            placeholder="Min total attended"
            value={minAttendedFilter}
            onChange={(e) => setMinAttendedFilter(e.target.value)}
          />
          <select
            style={styles.filterInput}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div style={styles.filtersAction}>
          <Button onClick={applyFilters}>Apply Filters</Button>
          <Button variant="secondary" onClick={resetFilters}>Reset</Button>
        </div>
      </Card>

      <div style={{ marginTop: '20px' }}>
        {users.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No users found"
            description="No users match the current filters."
          />
        ) : (
          <Table columns={columns} data={users} emptyMessage="No users found" />
        )}
      </div>

      <Modal
        isOpen={showToggleModal}
        onClose={() => setShowToggleModal(false)}
        title={selectedUser?.isActive ? 'Deactivate User' : 'Activate User'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowToggleModal(false)}>
              Cancel
            </Button>
            <Button
              variant={selectedUser?.isActive ? 'danger' : 'primary'}
              onClick={handleToggleStatus}
            >
              {selectedUser?.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          </>
        }
      >
        <p style={{ color: theme.colors.text, lineHeight: '1.6' }}>
          {selectedUser?.isActive
            ? `Deactivate ${selectedUser?.name}? Inactive users cannot sign in, join streams, or receive emails.`
            : `Activate ${selectedUser?.name}? The account will be able to sign in and join streams again.`}
        </p>
      </Modal>
    </div>
  );
};

export default AdminUsers;
